import type { Action, Dispatch, ReducersMapObject, Store } from "redux";
import type { TypedUseSelectorHook } from "react-redux";
import { useSelector, useDispatch } from "react-redux";
import type { CommonActionsType, CommonReducerType } from "../../../../common-deprecated/redux/commonStore";
import UtilReducer from "../../../../common-deprecated/redux/reducers/UtilReducer";
import CommonSettingsReducer from "../../../../common-deprecated/redux/reducers/CommonSettingsReducer";
import ModalReducer from "../../../../common-deprecated/redux/reducers/ModalReducer";
import { getInitToolkitStore } from "../../../../common-deprecated/redux/storeHelpers";
import type { CarConfigSettingsActionTypes, CarConfigSettingsType } from "./slices/CarConfigSettingsSlice";
import { CarConfigSettingsSlice } from "./slices/CarConfigSettingsSlice";
import type { CarConfigItemActionTypes } from "./slices/CarConfigItemSlice";
import { CarConfigItemSlice } from "./slices/CarConfigItemSlice";
import type { CreateLogicType, ItemsType } from "../../../../common-deprecated/redux/typeHelpers";
import type { CarConfigCarActionTypes } from "./slices/CarConfigCarSlice";
import { CarConfigCarSlice } from "./slices/CarConfigCarSlice";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { CarConfigSaveActionsTypes, SaveStateType } from "./slices/CarConfigSaveSlice";
import { CarConfigSaveSlice } from "./slices/CarConfigSaveSlice";
import saveLogic, { serverLogic as saveServerLogic } from "./logic/SaveLogic";
import financeLogic, { financeInsuranceServerLogic as financeServerLogic } from "./logic/FinanceInsuranceLogic";
import type { CarConfigBasketActionsTypes } from "./slices/CarConfigBasketSlice";
import { CarConfigBasketSlice } from "./slices/CarConfigBasketSlice";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import type { CarConfigFinanceActionsType, FinanceRateStateType } from "./slices/CarConfigFinanceSlice";
import { CarConfigFinanceSlice } from "./slices/CarConfigFinanceSlice";
import basketLogic, { basketServerLogic } from "./logic/BasketLogic";
import NotificationReducer from "../../../../common-deprecated/redux/reducers/NotificationReducer";
import type { NotificationActionsType } from "../../../../common-deprecated/redux/actions/NotificationActions";
import type { CarConfigGradesActionTypes, GradesStateType } from "./slices/CarConfigGradesSlice";
import { CarConfigGradesSlice } from "./slices/CarConfigGradesSlice";
import type { CarConfigInsuranceActionsType } from "./slices/CarConfigInsuranceSlice";
import { CarConfigInsuranceSlice } from "./slices/CarConfigInsuranceSlice";
import type {
    CarConfigExternalPriceActionsType,
    CarConfigExternalPriceStateType,
} from "./slices/carConfigExternalPriceSlice";
import { CarConfigExternalPriceSlice } from "./slices/carConfigExternalPriceSlice";
import utilLogic from "./logic/UtilLogic";
import externalPriceLogic from "./logic/ExternalPriceLogic";
import type { CarConfigMotorizationActionTypes, CarConfigMotorizationType } from "./slices/CarConfigMotorizationSlice";
import { CarConfigMotorizationSlice } from "./slices/CarConfigMotorizationSlice";
import type { CarConfigExtraFilterActionType, ExtraFilterType } from "./slices/CarConfigExtraFilterSlice";
import { CarConfigExtraFilterSlice } from "./slices/CarConfigExtraFilterSlice";
import type { InsuranceRateStateType } from "../../../shared-logic/types/InsuranceTypes";
import type { CarConfigEtaActionTypes } from "./slices/CarConfigEtaSlice";
import { CarConfigEtaSlice } from "./slices/CarConfigEtaSlice";
import type { EtaStateType } from "../../../../common-deprecated/types/CommonTypes";
import etaLogic from "./logic/EtaLogic";
import retailerCarConfigNavLogic from "./logic/RetailerCarConfigNavLogic";
import type { CarConfigSpecsSearchActionsTypes, CarConfigSpecsSearchType } from "./slices/CarConfigSpecsSearchSlice";
import { CarConfigSpecsSearchSlice } from "./slices/CarConfigSpecsSearchSlice";
import type { TradeInAemExternalDataReducerType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import TradeInAemExternalDataReducer from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import type { TradeInAemExternalDataActions } from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import tradeInAemExternalDataLogic from "./logic/TradeInAemExternalDataLogic";
import availableInOtherGradesLogic from "./logic/AvailableInOtherGradesLogic";
import type { CarConfigAtmosphereActionTypes, CarConfigAtmosphereType } from "./slices/CarConfigAtmosphereSlice";
import { CarConfigAtmosphereSlice } from "./slices/CarConfigAtmosphereSlice";
import type { CarConfigCompareSpecsActionTypes, CarConfigCompareSpecsType } from "./slices/CarConfigCompareSpecsSlice";
import { CarConfigCompareSpecsSlice } from "./slices/CarConfigCompareSpecsSlice";
import type { CarConfigAvailableInOtherGradesTypes } from "./slices/CarConfigAvailableInOtherGradesSlice";
import { CarConfigAvailableInOtherGradesSlice } from "./slices/CarConfigAvailableInOtherGradesSlice";
import type { CarConfigAvailableInOtherGradesType } from "../../../shared-logic/types/AvailableInOtherGradesType";
import type { CarConfigSpecActionTypes, SpecStateType } from "./slices/CarConfigSpecSlice";
import { CarConfigSpecSlice } from "./slices/CarConfigSpecSlice";
import type { CarConfigNavigationActionType, CarConfigNavigationType } from "./slices/CarConfigNavigationSlice";
import { CarConfigNavigationSlice } from "./slices/CarConfigNavigationSlice";
import ecoConsumptionLogic from "../../../shared-modules/eco-consumption-section/redux/logic/EcoConsumptionLogic";
import type {
    EcoConsumptionActionsType,
    EcoConsumptionStateType,
} from "../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import EcoConsumptionReducer from "../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import type {
    CarConfigSpinNavigationActionsType,
    CarConfigSpinNavigationStateType,
} from "./slices/CarConfigSpinNavigationSlice";
import { CarConfigSpinNavigationSlice } from "./slices/CarConfigSpinNavigationSlice";

export type CarConfigStateType = CommonReducerType & {
    [CarConfigSpecSlice.name]: SpecStateType;
    [CarConfigSettingsSlice.name]: CarConfigSettingsType;
    [CarConfigCarSlice.name]: { initialized: boolean } & CarType;
    [CarConfigItemSlice.name]: ItemsType;
    [CarConfigSaveSlice.name]: SaveStateType;
    [CarConfigBasketSlice.name]: BasketType;
    [CarConfigFinanceSlice.name]: FinanceRateStateType;
    [CarConfigInsuranceSlice.name]: InsuranceRateStateType;
    [CarConfigExternalPriceSlice.name]: CarConfigExternalPriceStateType;
    [CarConfigGradesSlice.name]: GradesStateType;
    [CarConfigExtraFilterSlice.name]: ExtraFilterType;
    [CarConfigMotorizationSlice.name]: CarConfigMotorizationType;
    [CarConfigEtaSlice.name]: EtaStateType;
    [CarConfigSpecsSearchSlice.name]: CarConfigSpecsSearchType;
    [CarConfigAtmosphereSlice.name]: CarConfigAtmosphereType;
    [CarConfigCompareSpecsSlice.name]: CarConfigCompareSpecsType;
    [CarConfigAvailableInOtherGradesSlice.name]: CarConfigAvailableInOtherGradesType;
    [CarConfigNavigationSlice.name]: CarConfigNavigationType;
    [CarConfigSpinNavigationSlice.name]: CarConfigSpinNavigationStateType;
    tradeInAemExternalData: TradeInAemExternalDataReducerType;
    ecoConsumption: EcoConsumptionStateType;
};

export type CarConfigActionType =
    | CommonActionsType
    | CarConfigSpecActionTypes
    | CarConfigSettingsActionTypes
    | CarConfigItemActionTypes
    | CarConfigCarActionTypes
    | CarConfigSaveActionsTypes
    | CarConfigBasketActionsTypes
    | CarConfigFinanceActionsType
    | CarConfigInsuranceActionsType
    | CarConfigExternalPriceActionsType
    | CarConfigGradesActionTypes
    | CarConfigExtraFilterActionType
    | NotificationActionsType
    | CarConfigEtaActionTypes
    | CarConfigMotorizationActionTypes
    | CarConfigSpecsSearchActionsTypes
    | TradeInAemExternalDataActions
    | CarConfigAtmosphereActionTypes
    | CarConfigCompareSpecsActionTypes
    | CarConfigAvailableInOtherGradesTypes
    | CarConfigNavigationActionType
    | CarConfigSpinNavigationActionsType
    | EcoConsumptionActionsType;

export type CarConfigDispatchType = Dispatch<CarConfigActionType>;

export type CarConfigLogicType<ActionType extends Action> = CreateLogicType<
    ActionType,
    CarConfigStateType,
    CarConfigDispatchType
>;

export type CarConfigStoreType = Store<CarConfigStateType, CarConfigActionType>;

export const useCarConfigDispatch = useDispatch<CarConfigDispatchType>;

export const useCarConfigSelector: TypedUseSelectorHook<CarConfigStateType> = useSelector;

// Any because: carConfigReducers contains reducers that can handle actions of any kind
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const carConfigReducers: ReducersMapObject<CarConfigStateType, any> = {
    utils: UtilReducer,
    commonSettings: CommonSettingsReducer,
    modal: ModalReducer,
    notification: NotificationReducer,
    tradeInAemExternalData: TradeInAemExternalDataReducer, // Needed for integrated Trade-in
    [CarConfigSettingsSlice.name]: CarConfigSettingsSlice.reducer,
    [CarConfigSpecSlice.name]: CarConfigSpecSlice.reducer,
    [CarConfigItemSlice.name]: CarConfigItemSlice.reducer,
    [CarConfigCarSlice.name]: CarConfigCarSlice.reducer,
    [CarConfigSaveSlice.name]: CarConfigSaveSlice.reducer,
    [CarConfigBasketSlice.name]: CarConfigBasketSlice.reducer,
    [CarConfigFinanceSlice.name]: CarConfigFinanceSlice.reducer,
    [CarConfigMotorizationSlice.name]: CarConfigMotorizationSlice.reducer,
    [CarConfigInsuranceSlice.name]: CarConfigInsuranceSlice.reducer,
    [CarConfigExternalPriceSlice.name]: CarConfigExternalPriceSlice.reducer,
    [CarConfigExtraFilterSlice.name]: CarConfigExtraFilterSlice.reducer,
    [CarConfigGradesSlice.name]: CarConfigGradesSlice.reducer,
    [CarConfigEtaSlice.name]: CarConfigEtaSlice.reducer,
    [CarConfigSpecsSearchSlice.name]: CarConfigSpecsSearchSlice.reducer,
    [CarConfigNavigationSlice.name]: CarConfigNavigationSlice.reducer,
    [CarConfigAvailableInOtherGradesSlice.name]: CarConfigAvailableInOtherGradesSlice.reducer,
    [CarConfigSpinNavigationSlice.name]: CarConfigSpinNavigationSlice.reducer,
    ecoConsumption: EcoConsumptionReducer,
    // LBX specific reducers
    [CarConfigAtmosphereSlice.name]: CarConfigAtmosphereSlice.reducer,
    [CarConfigCompareSpecsSlice.name]: CarConfigCompareSpecsSlice.reducer,
};

export const carConfigServerStore = getInitToolkitStore(carConfigReducers, [
    ...saveServerLogic,
    ...basketServerLogic,
    ...financeServerLogic,
    ...ecoConsumptionLogic,
]);

export const carConfigClientStore = getInitToolkitStore(carConfigReducers, [
    ...etaLogic,
    ...basketLogic,
    ...financeLogic,
    ...saveLogic,
    ...utilLogic,
    ...externalPriceLogic,
    ...tradeInAemExternalDataLogic,
    ...availableInOtherGradesLogic,
    ...retailerCarConfigNavLogic,
    ...ecoConsumptionLogic,
]);
